import { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FiEdit2, FiMapPin, FiPlus, FiTrash2 } from 'react-icons/fi';
import { ValidationError } from 'yup';
import { FieldHint, FieldShell } from './fields';
import { maskCep, onlyDigits } from './masks';
import { lookupCep } from './personApi';
import { AddressItem, addressSchema, newKey } from './personForm';
import * as S from './styles';

const emptyAddress = (): AddressItem => ({
  key: newKey(),
  address_zip_code: '',
  address_street: '',
  address_number: '',
  address_complement: '',
  address_district: '',
  city: '',
  uf: '',
});

const hasContent = (address: AddressItem) =>
  [
    address.address_zip_code,
    address.address_street,
    address.address_number,
    address.address_complement,
    address.address_district,
    address.city,
    address.uf,
  ].some((value) => value.trim() !== '');

interface Props {
  items: AddressItem[];
  onChange: (items: AddressItem[]) => void;
  /** Avisa o formulário quando há um endereço preenchido e não adicionado. */
  onPendingChange: (pending: boolean) => void;
  error?: string;
  disabled?: boolean;
}

export function AddressSection({ items, onChange, onPendingChange, error, disabled }: Props) {
  const [draft, setDraft] = useState<AddressItem | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [cepHint, setCepHint] = useState<FieldHint | null>(null);
  const lastCep = useRef('');
  const numberRef = useRef<HTMLInputElement>(null);
  const isEditing = Boolean(draft && items.some((item) => item.key === draft.key));

  useEffect(() => {
    onPendingChange(Boolean(draft && hasContent(draft)));
  }, [draft, onPendingChange]);

  const cepDigits = onlyDigits(draft?.address_zip_code);

  useEffect(() => {
    if (cepDigits.length !== 8 || cepDigits === lastCep.current) return;
    lastCep.current = cepDigits;
    let active = true;

    setCepHint({ tone: 'info', text: 'Buscando endereço…', loading: true });
    lookupCep(cepDigits)
      .then((result) => {
        if (!active) return;
        if (!result) {
          setCepHint({ tone: 'warning', text: 'CEP não encontrado. Preencha o endereço manualmente.' });
          return;
        }
        setDraft((current) =>
          current
            ? {
                ...current,
                address_street: result.address_street || current.address_street,
                address_district: result.address_district || current.address_district,
                address_complement: current.address_complement || result.address_complement,
                city: result.city || current.city,
                uf: result.uf || current.uf,
              }
            : current,
        );
        setErrors({});
        setCepHint({ tone: 'success', text: 'Endereço preenchido pelo CEP.' });
        numberRef.current?.focus();
      })
      .catch(() => {
        if (active) {
          setCepHint({ tone: 'warning', text: 'Não foi possível consultar o CEP agora. Preencha manualmente.' });
        }
      });

    return () => {
      active = false;
    };
  }, [cepDigits]);

  const openEditor = (address?: AddressItem) => {
    lastCep.current = onlyDigits(address?.address_zip_code);
    setDraft(address ? { ...address } : emptyAddress());
    setErrors({});
    setCepHint(null);
  };

  const closeEditor = () => {
    setDraft(null);
    setErrors({});
    setCepHint(null);
  };

  const update = (field: keyof AddressItem, value: string) => {
    setDraft((current) => (current ? { ...current, [field]: value } : current));
    if (errors[field]) setErrors(({ [field]: _removed, ...rest }) => rest);
  };

  const confirm = () => {
    if (!draft) return;
    try {
      addressSchema.validateSync(draft, { abortEarly: false });
    } catch (validation) {
      const next: Record<string, string> = {};
      (validation as ValidationError).inner.forEach((issue) => {
        if (issue.path && !next[issue.path]) next[issue.path] = issue.message;
      });
      setErrors(next);
      return;
    }

    const normalized = { ...draft, uf: draft.uf.trim().toUpperCase() };
    onChange(
      isEditing
        ? items.map((item) => (item.key === draft.key ? normalized : item))
        : [...items, normalized],
    );
    closeEditor();
  };

  const remove = (key: string) => {
    onChange(items.filter((item) => item.key !== key));
    if (draft?.key === key) closeEditor();
  };

  return (
    <S.Card id="secao-enderecos" aria-labelledby="titulo-enderecos">
      <S.CardTitle id="titulo-enderecos">
        <span>
          Endereços<small>{items.length > 0 ? `${items.length} cadastrado(s)` : 'opcional'}</small>
        </span>
      </S.CardTitle>

      {error && (
        <S.Banner $tone="error" role="alert" style={{ marginBottom: 10 }}>
          {error}
        </S.Banner>
      )}

      {items.length === 0 && !draft && <S.Empty>Nenhum endereço adicionado.</S.Empty>}

      {items.length > 0 && (
        <S.ItemList>
          {items.map((item) => (
            <S.Item key={item.key} $editing={draft?.key === item.key} data-testid="address-item">
              <div>
                <strong>
                  {item.address_street}, {item.address_number}
                  {item.address_complement ? ` – ${item.address_complement}` : ''}
                </strong>
                <span>
                  {[item.address_district, [item.city, item.uf].filter(Boolean).join('/')]
                    .filter(Boolean)
                    .join(' · ')}
                  {item.address_zip_code ? ` · CEP ${item.address_zip_code}` : ''}
                </span>
              </div>
              <S.ItemActions>
                <button
                  type="button"
                  title="Editar endereço"
                  aria-label={`Editar endereço ${item.address_street}`}
                  onClick={() => openEditor(item)}
                  disabled={disabled}
                >
                  <FiEdit2 size={14} />
                </button>
                <button
                  type="button"
                  className="danger"
                  title="Remover endereço"
                  aria-label={`Remover endereço ${item.address_street}`}
                  onClick={() => remove(item.key)}
                  disabled={disabled}
                >
                  <FiTrash2 size={14} />
                </button>
              </S.ItemActions>
            </S.Item>
          ))}
        </S.ItemList>
      )}

      {draft ? (
        <S.Editor
          onKeyDown={(e) => {
            // Enter aqui confirma o endereço em vez de enviar o formulário inteiro.
            if (e.key === 'Enter') {
              e.preventDefault();
              confirm();
            }
          }}
        >
          <S.CardTitle as="div" style={{ fontSize: 13 }}>
            <span>
              <FiMapPin size={13} style={{ marginRight: 6 }} />
              {isEditing ? 'Editar endereço' : 'Novo endereço'}
            </span>
          </S.CardTitle>
          <S.Grid $cols={4}>
            <FieldShell label="CEP" required error={errors.address_zip_code} hint={cepHint}>
              <input
                aria-label="CEP"
                inputMode="numeric"
                placeholder="00000-000"
                value={draft.address_zip_code}
                onChange={(e) => update('address_zip_code', maskCep(e.target.value))}
                autoFocus
              />
            </FieldShell>
            <S.Span $span={2}>
              <FieldShell label="Logradouro" required error={errors.address_street}>
                <input
                  aria-label="Logradouro"
                  placeholder="Rua, avenida…"
                  value={draft.address_street}
                  onChange={(e) => update('address_street', e.target.value)}
                />
              </FieldShell>
            </S.Span>
            <FieldShell label="Número" required error={errors.address_number}>
              <input
                ref={numberRef}
                aria-label="Número"
                placeholder="Ex.: 120 ou S/N"
                value={draft.address_number}
                onChange={(e) => update('address_number', e.target.value)}
              />
            </FieldShell>
            <FieldShell label="Complemento">
              <input
                aria-label="Complemento"
                placeholder="Apto, sala…"
                value={draft.address_complement}
                onChange={(e) => update('address_complement', e.target.value)}
              />
            </FieldShell>
            <FieldShell label="Bairro" required error={errors.address_district}>
              <input
                aria-label="Bairro"
                value={draft.address_district}
                onChange={(e) => update('address_district', e.target.value)}
              />
            </FieldShell>
            <FieldShell label="Cidade" required error={errors.city}>
              <input
                aria-label="Cidade"
                value={draft.city}
                onChange={(e) => update('city', e.target.value)}
              />
            </FieldShell>
            <FieldShell label="UF" required error={errors.uf}>
              <input
                aria-label="UF"
                maxLength={2}
                placeholder="SP"
                value={draft.uf}
                onChange={(e) => update('uf', e.target.value.replace(/[^a-zA-Z]/g, '').toUpperCase())}
              />
            </FieldShell>
          </S.Grid>
          <S.EditorActions>
            <Button variant="light" size="sm" onClick={closeEditor}>
              Descartar
            </Button>
            <Button variant="primary" size="sm" onClick={confirm}>
              {isEditing ? 'Atualizar endereço' : 'Adicionar endereço'}
            </Button>
          </S.EditorActions>
        </S.Editor>
      ) : (
        <S.AddButton type="button" onClick={() => openEditor()} disabled={disabled}>
          <FiPlus size={14} /> Adicionar endereço
        </S.AddButton>
      )}
    </S.Card>
  );
}
